/**
 * Cache e persistência de preferência de tabela por usuário (T38, TAB-10).
 *
 * Consome os tipos de `resolverColunas.ts` (T37) — ver `docs/preferencias-de-tabela.md`
 * §4 e `docs/contratos-backend.md` §1 (o contrato que `PreferenciasApi` obedece).
 * Não extrai nada de `tabelaDropdown.vue`: é peça nova, ainda sem consumidor
 * (o host passa a chamar isto na T39).
 *
 * **Sob Q2** (`spec.md`, confirmado 2026-09-03) o backend está fora desta
 * rodada: `criarPreferenciasApiLocal` é a implementação de hoje, atrás da
 * MESMA interface `PreferenciasApi` do contrato. Trocar para HTTP depois é
 * escrever outra implementação da interface e passá-la em `opcoes.api` —
 * nenhum consumidor muda.
 *
 * **`sessionStorage` (cache) ≠ `localStorage` (via `PreferenciasApi` local).**
 * O cache morre com a aba de propósito (nunca sobrevive a um re-login, nunca
 * vira fonte de verdade paralela — era esse o bug do `tabelas.ts` de hoje).
 * Quem persiste de fato, mesmo sem backend, é `criarPreferenciasApiLocal`,
 * que grava em `localStorage` sob uma chave por e-mail — é o "banco" de hoje,
 * substituível por rede amanhã sem tocar quem chama `useTabelaPreferencias`.
 *
 * **Escrita otimista, debounced, com 1 retry e falha silenciosa** (§4 do doc):
 * `salvar()` aplica local na hora, agenda a escrita ~800ms depois, e
 * `flush()` (saída de rota, `onBeforeUnmount` do host) faz a escrita pendente
 * na hora. Falha nunca gera aviso interruptivo — preferência de UI não merece interromper
 * o usuário; a escolha sobrevive na aba e volta a tentar na próxima interação.
 */
import { ref, type Ref } from "vue";
import { useSessionStorage, StorageSerializers } from "@vueuse/core";
import type { ColunaCatalogo, PreferenciaTabela } from "./resolverColunas";

/**
 * Contrato que o backend precisa respeitar quando existir
 * (`docs/contratos-backend.md` §1, B1.1-B1.6). `listar` = todas as
 * preferências do usuário (uma `GET`); `salvar` = upsert idempotente de uma
 * tabela; `remover` = "restaurar padrão".
 */
export interface PreferenciasApi {
  listar(): Promise<Record<string, PreferenciaTabela>>;
  salvar(tabelaId: string, pref: PreferenciaTabela): Promise<void>;
  remover(tabelaId: string): Promise<void>;
}

export const CHAVE_STORAGE_BASE = "preferenciasTabelas";

/**
 * Prefixo da chave de CACHE por aba (`sessionStorage`). Único lugar do
 * repositório que conhece o formato — `limparCachePreferencias` o reusa em vez
 * de repetir o literal. O mutante U1 da `docs/arquivo/.specs/features/migracao-feature-based/validation-D2.md` (chave de sessão sem
 * o e-mail) mostrou que duplicar esse literal é como a regra se perde.
 */
const PREFIXO_CACHE_SESSAO = "prefTabelas";

/**
 * Remove o cache de preferência desta aba.
 *
 * O único chamador de produção é **`store/user.ts`**, em `salvarUsuario` — a
 * troca de sessão. **Não é `AuthApi.logout`**, que é onde o §4 de
 * `docs/preferencias-de-tabela.md` pede ("Limpar a chave no logout"): tocar
 * `libs/AuthApi.ts` paga 24 warnings de `lint-staged` e exige abrir o
 * `@ts-nocheck` de um arquivo que é território da T66, cancelada (AD-038). O
 * desvio, o resíduo que ele deixa e a medição estão na nota de T39 no
 * `tasks.md`. Lacuna 7 da `docs/arquivo/.specs/features/migracao-feature-based/validation-D2.md`, e achado 1 e 6 da `docs/arquivo/.specs/features/migracao-feature-based/validation-D3.md`.
 *
 * Sem isso o cache atravessa a troca de sessão: `carregar()` lê `cache.value`
 * ANTES de qualquer `listar()`, então o próximo login na MESMA aba herdaria a
 * preferência de quem saiu. O `localStorage.clear()` do logout não alcança
 * isto — o cache é `sessionStorage`, de propósito (§4).
 *
 * **O parâmetro `email` não tem chamador de produção hoje**, e fica de
 * propósito: é a costura do dia em que o gancho voltar para o logout, que é o
 * único momento em que se sabe QUEM está saindo. Sem ele, varre o prefixo — o
 * comportamento seguro, porque preferência de UI não é dado que valha manter na
 * dúvida. Achado 11 da `docs/arquivo/.specs/features/migracao-feature-based/validation-D3.md`.
 */
export function limparCachePreferencias(email?: string | null): void {
  try {
    if (email) {
      sessionStorage.removeItem(`${PREFIXO_CACHE_SESSAO}:${email}`);
      return;
    }
    const alvos = Object.keys(sessionStorage).filter((chave) =>
      chave.startsWith(`${PREFIXO_CACHE_SESSAO}:`),
    );
    for (const chave of alvos) sessionStorage.removeItem(chave);
  } catch {
    // `sessionStorage` indisponível (aba privada, storage desabilitado). O
    // logout nunca pode falhar por causa de cache de preferência de coluna.
  }
}

/** Implementação local de `PreferenciasApi` (Q2) — um mapa por e-mail em `localStorage`. */
export function criarPreferenciasApiLocal(email: string): PreferenciasApi {
  const chave = `${CHAVE_STORAGE_BASE}:${email}`;

  function lerTudo(): Record<string, PreferenciaTabela> {
    try {
      const bruto = localStorage.getItem(chave);
      return bruto ? (JSON.parse(bruto) as Record<string, PreferenciaTabela>) : {};
    } catch {
      return {};
    }
  }

  function escreverTudo(mapa: Record<string, PreferenciaTabela>): void {
    localStorage.setItem(chave, JSON.stringify(mapa));
  }

  return {
    async listar() {
      return lerTudo();
    },
    async salvar(tabelaId, pref) {
      const mapa = lerTudo();
      mapa[tabelaId] = pref;
      escreverTudo(mapa);
    },
    async remover(tabelaId) {
      const mapa = lerTudo();
      delete mapa[tabelaId];
      escreverTudo(mapa);
    },
  };
}

const ATRASO_ESCRITA_MS = 800;

/**
 * Debounce com `flush()` de verdade — `useDebounceFn` do `@vueuse/core` (o
 * padrão do resto desta pasta, T30) não expõe `flush`/`cancel` na assinatura
 * pública, e T38 precisa de descarregar a escrita pendente na saída de rota
 * sem esperar o temporizador. `setTimeout`/`clearTimeout` batem essa
 * necessidade em poucas linhas, sem trazer lib nova.
 */
function criarEscritaComDebounce(
  escrever: () => void | Promise<void>,
  atrasoMs = ATRASO_ESCRITA_MS,
) {
  let temporizador: ReturnType<typeof setTimeout> | null = null;
  let pendente = false;

  function agendar(): void {
    pendente = true;
    if (temporizador) clearTimeout(temporizador);
    temporizador = setTimeout(() => {
      temporizador = null;
      pendente = false;
      void escrever();
    }, atrasoMs);
  }

  function flush(): void {
    if (temporizador) {
      clearTimeout(temporizador);
      temporizador = null;
    }
    if (pendente) {
      pendente = false;
      void escrever();
    }
  }

  return { agendar, flush };
}

export interface OpcoesTabelaPreferencias {
  /** E-mail do usuário logado. Entra na chave do cache — troca de usuário nunca herda o registro de outro. */
  email: string;
  /** Implementação de `PreferenciasApi`. Default: storage local (Q2). */
  api?: PreferenciasApi;
  /** Observabilidade (log/Sentry) da falha de escrita depois do retry — NUNCA um aviso interruptivo ao usuário. */
  aoFalharEscrita?: (erro: unknown) => void;
}

export function useTabelaPreferencias(tabelaId: string, opcoes: OpcoesTabelaPreferencias) {
  const api = opcoes.api ?? criarPreferenciasApiLocal(opcoes.email);

  /** Cache por ABA: chave inclui o e-mail (§4 do doc) — morre com a aba, nunca sobrevive a um re-login. */
  const cache = useSessionStorage<Record<string, PreferenciaTabela> | null>(
    `${PREFIXO_CACHE_SESSAO}:${opcoes.email}`,
    null,
    { serializer: StorageSerializers.object },
  );

  /** Preferência desta tabela. `null` = ausência de registro = padrão do código (P4) — nunca decidido aqui. */
  const preferencia: Ref<PreferenciaTabela | null> = ref(null);

  async function carregarTudo(): Promise<Record<string, PreferenciaTabela>> {
    if (cache.value) return cache.value;
    cache.value = await api.listar(); // 1 requisição por aba (zero round-trip nas seguintes)
    return cache.value;
  }

  /** Carrega a preferência desta tabela a partir do cache (ou de `api.listar()` na primeira vez da aba). */
  async function carregar(): Promise<PreferenciaTabela | null> {
    const tudo = await carregarTudo();
    preferencia.value = tudo[tabelaId] ?? null;
    return preferencia.value;
  }

  async function escreverAgora(): Promise<void> {
    const alvo = preferencia.value;
    if (!alvo) return;
    try {
      await api.salvar(tabelaId, alvo);
    } catch {
      try {
        await api.salvar(tabelaId, alvo); // 1 retry — política de §4 do doc
      } catch (erro) {
        // Falha de rede: mantém o LOCAL, sem exceção subindo e sem aviso interruptivo — a
        // escolha sobrevive na aba e volta a ser persistida na próxima interação.
        opcoes.aoFalharEscrita?.(erro);
      }
    }
  }

  const { agendar, flush } = criarEscritaComDebounce(escreverAgora);

  /**
   * Aplica `nova` LOCALMENTE agora (otimista — a UI não espera rede) e agenda
   * a escrita remota com debounce: cinco chamadas seguidas viram uma só
   * escrita, com o estado mais recente no momento em que o temporizador dispara.
   */
  function salvar(nova: PreferenciaTabela): void {
    preferencia.value = nova;
    if (!cache.value) cache.value = {};
    cache.value[tabelaId] = nova;
    agendar();
  }

  /** Chaves de folha do catálogo inteiro (top-level e dentro de `children`). */
  function chavesDoCatalogo(catalogo: readonly ColunaCatalogo[]): Set<string> {
    const chaves = new Set<string>();
    for (const item of catalogo) {
      if (item.children?.length) {
        for (const folha of item.children) if (folha.key) chaves.add(folha.key);
      } else if (item.key) {
        chaves.add(item.key);
      }
    }
    return chaves;
  }

  /**
   * Quantas chaves do catálogo não estão na preferência salva — "3 colunas
   * novas disponíveis" (§4 do doc). Sem preferência salva não há nada a
   * anunciar como NOVO: quem nunca configurou já vê o catálogo inteiro.
   */
  function contarColunasNovas(catalogo: readonly ColunaCatalogo[]): number {
    if (!preferencia.value?.colunas.length) return 0;
    const configuradas = new Set(preferencia.value.colunas.map((c) => c.chave));
    const conhecidas = new Set(preferencia.value.conhecidas); // escondida (conhecida) não é nova
    let novas = 0;
    for (const chave of chavesDoCatalogo(catalogo))
      if (!configuradas.has(chave) && !conhecidas.has(chave)) novas++;
    return novas;
  }

  return {
    preferencia,
    carregar,
    salvar,
    flush,
    contarColunasNovas,
  };
}
